import type { GroupedRewardType } from '@/queries/rewards/rewards.schema';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Button } from '@/components/ui/Button/button';
import openModal from '@/modals/utils/openModal';
import { cn } from '@/utils/cn';
import { getDailyBonusUpToByIndex } from '../utils/dailyBonusUpToAmounts.utils';
import '../styles/dailyBonusItem.css';

export type DailyBonusCardState = 'ready' | 'claimed' | 'locked';
export type DailyBonusVariantCard = 'desktop' | 'mobile' | 'responsive';

interface Props {
  type?: GroupedRewardType;
  index?: number;
  rewardAmount: number;
  bonusDays: number;
  state: DailyBonusCardState;
  redirectToShop?: boolean;
  onClick?: () => void;
  className?: string;
  variantCard?: DailyBonusVariantCard;
  amountClassName?: string;
}

const stateRingByType: Record<DailyBonusCardState, string> = {
  ready: 'active-daily-reward-card active-daily-reward-shadow',
  claimed: 'active-daily-reward-card inactive-daily-reward-shadow',
  locked: 'inactive-daily-reward-card locked-daily-reward-card-shadow',
};

const getRewardImage = (amount: number) => `/assets/image/daily-rewards/daily-reward-${amount}-sc@2x.webp`;

const getImgVariants = cva(
  'relative z-1',
  {
    variants: {
      variant: {
        mobile: 'w-15 h-15',
        desktop: 'w-10 h-10',
        responsive: 'w-15 h-15 lg:w-10 lg:h-10',
      },
    },
    defaultVariants: {
      variant: 'responsive',
    },
  },
);

const DailyBonusCard = ({
  type,
  index,
  variantCard = 'responsive',
  rewardAmount,
  bonusDays,
  state,
  redirectToShop = false,
  onClick,
  className,
  amountClassName,
}: Props) => {
  const t = useTranslations('daily-bonus');
  const isReady = state === 'ready';
  const isLocked = state === 'locked';
  const isClaimed = state === 'claimed';
  const isDailyBonus = type === 'daily-bonus';
  const isClaimAvailable = isReady && typeof onClick === 'function';

  const isShopCard = isDailyBonus && isLocked;
  const dailyCounter = Math.min(7, Math.max(1, bonusDays));
  const rewardUpToByIndex
    = typeof index === 'number' ? getDailyBonusUpToByIndex(index, rewardAmount) : rewardAmount;

  const handleCardClick = () => {
    if (isShopCard || !isReady || redirectToShop) {
      openModal('daily-bonus-info-offer');
      return;
    }

    if (!isClaimAvailable) {
      return;
    }

    onClick?.();
  };

  const getButtonTextKey = () => {
    if (isClaimed) {
      return 'wait';
    }
    return isReady ? 'claim' : 'unlock';
  };

  if (isShopCard) {
    return null;
  }

  return (
    <div
      tabIndex={0}
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        handleCardClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          handleCardClick();
        }
      }}
      className={cn(
        'relative w-full min-w-22.5 cursor-pointer h-35.5 lg:min-w-18.5 lg:h-30.5 justify-end isolate items-center overflow-visible flex flex-col text-left daily-reward-active-stroke rounded-2xl p-[0.313rem] pt-2',
        isShopCard ? '' : stateRingByType[state],
        isLocked && index === 1 && 'shadow-none',
        className,
      )}
    >
      {!isLocked && (
        <span
          className={
            cn(
              'absolute size-6 z-1 text-xs flex justify-center items-center top-[-0.313rem] right-[-0.313rem] font-semibold leading-4 text-center rounded-full text-light',
              isReady ? 'active-daily-reward-badge' : 'inactive-daily-reward-badge',
            )
          }
        >
          {isDailyBonus ? dailyCounter : bonusDays}
        </span>
      )}
      <Image
        width={120}
        height={120}
        alt={t('alt-daily-img')}
        className={getImgVariants({ variant: variantCard })}
        src={getRewardImage(rewardUpToByIndex)}
      />
      <div className={cn('flex flex-col', (isLocked && isReady) && 'lg:mt-4.5')}>
        <span className="relative z-1 font-normal text-[0.625rem] leading-2.5 text-light flex justify-center items-center">{t('up-to')}</span>
        <div className={cn('relative z-1 font-medium text-lg lg:text-sm leading-5 flex justify-center items-center text-light', amountClassName)}>
          {rewardUpToByIndex}
          {' '}
          SC
        </div>
      </div>

      <Button
        type="button"
        className={cn('h-7 mt-2 lg:mt-1 text-sm font-medium w-full', variantCard === 'desktop' && 'mt-1!',
        )}
        variant={isReady || isClaimed ? 'primary' : 'secondary'}
        disabled={isClaimed}
      >
        {t(getButtonTextKey())}
      </Button>
    </div>

  );
};

export default DailyBonusCard;
